import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, PenLine, ChevronLeft, ChevronRight, FileText, Mail, Move, Undo2, Check, Heading, ArrowRight, CheckCheck, Minimize2, AlertTriangle } from 'lucide-react';
import { PrioBadge } from './PrioCriteriaPicker';
import { Switch } from './ui/switch';

// Flyers are pre-written stock (QR printed separately) — the preview covers only what the iAuto writes per letter.
const DOC_TABS = [
  ['letter', 'Brief', FileText],
  ['envelope', 'Umschlag', Mail],
];
const DOC_LABELS = { letter: 'Brief', flyer: 'Flyer', envelope: 'Umschlag' };
const ADJ_ELEMENTS = {
  header: { label: 'Headline', cls: 'border-sky-400', badge: 'bg-sky-500', posKey: 'pos_header', doc: 'letter' },
  date: { label: 'Datum', cls: 'border-amber-400', badge: 'bg-amber-500', posKey: 'pos_date', doc: 'letter' },
  body: { label: 'Text', cls: 'border-emerald-400', badge: 'bg-emerald-500', posKey: null, doc: 'letter' },
  signature: { label: 'Unterschrift', cls: 'border-purple-400', badge: 'bg-purple-500', posKey: 'pos_signature', doc: 'letter' },
  env_addr: { label: 'Empfängeradresse', cls: 'border-rose-400', badge: 'bg-rose-500', posKey: 'env_pos_addr', doc: 'envelope' },
  env_addr_fixed: { label: 'Empfängeradresse', cls: 'border-rose-400', badge: 'bg-rose-500', posKey: 'env_pos_addr', doc: 'envelope', rotated: true },
};
const DOC_DIMS = { letter: { w: 210, h: 297 }, flyer: { w: 210, h: 297 }, envelope: { w: 324, h: 229 } };

export const PrintPreviewDialog = ({ letter, onClose, onConfirm, confirming, mailing, onHeadlineChange,
  checks, onCheckChanged, onNextRecipient, progress }) => {
  const [doc, setDoc] = useState('letter');
  const [svg, setSvg] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adjust, setAdjust] = useState(false);
  const [ov, setOv] = useState({});
  const [fontSize, setFontSize] = useState(6);
  const [elements, setElements] = useState({});
  const [autoFit, setAutoFit] = useState(null);
  const [letterFit, setLetterFit] = useState(null);
  const elementsRef = useRef({});
  const [savingOv, setSavingOv] = useState(false);
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const pageRef = useRef(null);
  const ovRef = useRef({});
  const pageNumRef = useRef(1);
  const [headlineOn, setHeadlineOn] = useState(false);
  const [hlBusy, setHlBusy] = useState(false);
  const [hlRefresh, setHlRefresh] = useState(0);
  const [svgDims, setSvgDims] = useState(null);
  const [areaEl, setAreaEl] = useState(null);
  const [area, setArea] = useState({ w: 0, h: 0 });
  const [localChecks, setLocalChecks] = useState({});
  const [checkBusy, setCheckBusy] = useState(false);

  useEffect(() => {
    setPage(1); setDoc('letter'); setAdjust(false); setOv({}); ovRef.current = {};
    setHeadlineOn(!!letter?.include_headline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letter?.id]);

  useEffect(() => {
    if (letter?.id) setLocalChecks({ ...(checks?.[letter.id] || {}) });
  }, [letter?.id, checks]);

  // Measure the preview area so the page always fits — buttons stay visible at any zoom level
  useEffect(() => {
    if (!areaEl) return;
    const measure = () => setArea({ w: areaEl.clientWidth, h: areaEl.clientHeight });
    const ro = new ResizeObserver(measure);
    ro.observe(areaEl);
    measure();
    return () => ro.disconnect();
  }, [areaEl]);

  const toggleHeadline = async (next) => {
    setHlBusy(true);
    try {
      await api.updateLetter(letter.id, { include_headline: next });
      setHeadlineOn(next);
      setHlRefresh(n => n + 1);
      onHeadlineChange?.();
      toast.success(next ? 'Überschrift wird mitgeschrieben' : 'Brief wird ohne Überschrift geschrieben');
    } catch (err) { toast.error(err.response?.data?.detail || 'Überschrift konnte nicht umgestellt werden'); }
    finally { setHlBusy(false); }
  };

  useEffect(() => {
    if (!letter || adjust) return;
    setLoading(true);
    setError('');
    api.getPrintPreview(letter.id, page, doc)
      .then(r => {
        const m = String(r.data).match(/width="([\d.]+)mm" height="([\d.]+)mm"/);
        setSvgDims(m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null);
        setSvg(String(r.data).replace(/width="[\d.]+mm" height="[\d.]+mm"/, 'width="100%"'));
        setPages(parseInt(r.headers['x-total-pages'] || '1', 10) || 1);
        try { setAutoFit(r.headers['x-env-autofit'] ? JSON.parse(r.headers['x-env-autofit']) : null); } catch { setAutoFit(null); }
        try { setLetterFit(r.headers['x-letter-fit'] ? JSON.parse(r.headers['x-letter-fit']) : null); } catch { setLetterFit(null); }
      })
      .catch(err => {
        setSvg('');
        try {
          const d = typeof err.response?.data === 'string' ? JSON.parse(err.response.data) : err.response?.data;
          setError(d?.detail || 'Preview failed');
        } catch { setError('Preview failed'); }
      })
      .finally(() => setLoading(false));
  }, [letter?.id, page, doc, adjust, letter, hlRefresh]);

  const dims = (doc === 'envelope' && svgDims) ? svgDims : (DOC_DIMS[doc] || DOC_DIMS.letter);
  const scale = area.w > 0 && area.h > 0 ? Math.min(area.w / dims.w, area.h / dims.h) : 0;
  const pageW = Math.floor(dims.w * scale);
  const pageH = Math.floor(dims.h * scale);

  const refreshAdjust = useCallback((o, p) => {
    if (!letter) return;
    setLoading(true);
    api.printPreviewSample({ doc, preview_letter_id: letter.id, page: p ?? pageNumRef.current, ...o })
      .then(r => {
        setSvg(String(r.data.svg).replace(/width="\d+mm" height="\d+mm"/, 'width="100%"'));
        elementsRef.current = r.data.elements || {};
        setElements(r.data.elements || {});
        setPages(r.data.pages || 1);
        setPage(r.data.page || 1);
        pageNumRef.current = r.data.page || 1;
        if (doc === 'letter' && r.data.fit) {
          setLetterFit(r.data.fit);
          if (r.data.fit.font_size) setFontSize(r.data.fit.font_size);
        }
        if (doc === 'envelope' && r.data.page_w && r.data.page_h) {
          setSvgDims({ w: r.data.page_w, h: r.data.page_h });
        }
      })
      .catch(() => setSvg(''))
      .finally(() => setLoading(false));
  }, [letter, doc]);

  const enableAdjust = async () => {
    try {
      const r = await api.getPrintLetterOverrides(letter.id);
      const o = r.data.overrides || {};
      setFontSize(r.data.font_size || 6);
      ovRef.current = o;
      setOv(o);
      setAdjust(true);
      pageNumRef.current = page;
      refreshAdjust(o, page);
    } catch { toast.error('Anpassungen konnten nicht geladen werden'); }
  };

  const exitAdjust = () => { setAdjust(false); setDrag(null); dragRef.current = null; };

  const setOvVal = (key, value) => {
    const next = { ...ovRef.current, [key]: value };
    ovRef.current = next;
    setOv(next);
    refreshAdjust(next);
  };

  const goPage = (p) => {
    pageNumRef.current = p;
    setPage(p);
    if (adjust) refreshAdjust(ovRef.current, p);
  };

  const mmFromEvent = (e) => {
    const r = pageRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width * dims.w, y: (e.clientY - r.top) / r.height * dims.h };
  };
  const startDrag = (key, e) => {
    e.preventDefault();
    const el = elements[key];
    if (!el || !pageRef.current) return;
    const m = mmFromEvent(e);
    const d = { key, dx: m.x - el.x, dy: m.y - el.y, x: el.x, y: el.y };
    dragRef.current = d;
    setDrag(d);
  };
  useEffect(() => {
    if (!adjust) return;
    const move = (e) => {
      const d = dragRef.current;
      if (!d || !pageRef.current) return;
      const m = mmFromEvent(e);
      const nd = { ...d, x: Math.max(0, Math.min(dims.w - 5, m.x - d.dx)), y: Math.max(0, Math.min(dims.h - 5, m.y - d.dy)) };
      dragRef.current = nd;
      setDrag(nd);
    };
    const up = () => {
      const d = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!d) return;
      if (d.key === 'body') {
        setOvVal('body_start_y', +(d.y + fontSize).toFixed(1));
      } else if (d.key === 'env_addr_fixed') {
        // rotated small envelope: page coords → reading-orientation coords for env_pos_addr
        const el = elementsRef.current.env_addr_fixed || {};
        const pos = (el.rot || 90) === 270
          ? { x: +(dims.h - d.y - (el.h || 0)).toFixed(1), y: +d.x.toFixed(1) }
          : { x: +d.y.toFixed(1), y: +(dims.w - d.x - (el.w || 0)).toFixed(1) };
        setOvVal('env_pos_addr', pos);
      } else if (ADJ_ELEMENTS[d.key]?.posKey) {
        setOvVal(ADJ_ELEMENTS[d.key].posKey, { x: +d.x.toFixed(1), y: +d.y.toFixed(1) });
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjust, fontSize, doc]);

  const saveOverrides = async () => {
    setSavingOv(true);
    try {
      await api.savePrintLetterOverrides(letter.id, ovRef.current);
      toast.success('Anpassungen für diesen Brief gespeichert');
      exitAdjust();
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSavingOv(false); }
  };

  const resetOverrides = () => {
    ovRef.current = {};
    setOv({});
    refreshAdjust({});
  };

  // ─── Check workflow: letter → envelope, then auto-advance to the next recipient ───
  const checkFlow = !!checks && !!onNextRecipient;
  const docCycle = ['letter', 'envelope'];
  const checkedCount = docCycle.filter(d => localChecks[d]).length;

  const advanceAfter = (nextChecks) => {
    const nextDoc = docCycle.find(d => !nextChecks[d]);
    if (nextDoc) {
      setDoc(nextDoc); setPage(1); exitAdjust();
    } else {
      toast.success(`${letter.company_name}: ${docCycle.map(d => DOC_LABELS[d]).join(' + ')} geprüft ✓ — weiter zum nächsten Empfänger`);
      onNextRecipient?.();
    }
  };

  const markChecked = async () => {
    setCheckBusy(true);
    try {
      await api.setPreviewCheck(letter.id, doc, true);
      const next = { ...localChecks, [doc]: true };
      setLocalChecks(next);
      onCheckChanged?.();
      advanceAfter(next);
    } catch (err) { toast.error(err.response?.data?.detail || 'Häkchen konnte nicht gesetzt werden'); }
    finally { setCheckBusy(false); }
  };

  const unmarkChecked = async () => {
    setCheckBusy(true);
    try {
      await api.setPreviewCheck(letter.id, doc, false);
      setLocalChecks({ ...localChecks, [doc]: false });
      onCheckChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setCheckBusy(false); }
  };

  const done = mailing?.[letter?.id] || {};
  const hasOv = Object.values(ov).some(v => v != null);
  const visibleBoxes = adjust
    ? Object.keys(ADJ_ELEMENTS).filter(k => ADJ_ELEMENTS[k].doc === doc && elements[k] && (elements[k].page || 1) === page)
    : [];

  return (
    <Dialog open={!!letter} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl w-[min(96vw,44rem)] h-[min(94vh,60rem)] flex flex-col gap-3 overflow-hidden"
        data-testid="print-preview-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-heading text-base flex items-center gap-2 flex-wrap">
            <PenLine className="w-4 h-4 text-purple-400" /> Handwriting preview — {letter?.company_name}
            <PrioBadge prio={letter?.prio} testid="print-preview-prio" className="text-[10px]" />
            {checkFlow && progress?.total > 0 && (
              <span className="text-[10px] font-mono text-muted-foreground font-normal" data-testid="print-check-progress">
                Empfänger {progress.index}/{progress.total} · {progress.checked} geprüft
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 items-center shrink-0">
          {DOC_TABS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => { setDoc(key); setPage(1); exitAdjust(); }}
              data-testid={`print-doc-tab-${key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border transition-colors ${doc === key
                ? 'border-purple-500/60 bg-purple-500/15 text-purple-200'
                : 'border-border text-muted-foreground hover:text-foreground'}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
              {checkFlow && localChecks[key] && (
                <Check className="w-3 h-3 text-emerald-400" data-testid={`print-tab-checked-${key}`} />
              )}
              {done[key] && <span className="text-emerald-400" title="Bereits geschrieben">✓</span>}
            </button>
          ))}
          {(doc === 'letter' || doc === 'envelope') && !adjust && (
            <Button size="sm" variant="outline" onClick={enableAdjust} data-testid="print-adjust-btn"
              className="h-7 text-[11px] rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500/10 ml-auto">
              <Move className="w-3 h-3" /> Nachjustieren
            </Button>
          )}
        </div>
        {doc === 'letter' && !adjust && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground border border-border rounded-sm px-3 py-2 shrink-0"
            data-testid="print-headline-row">
            <Heading className="w-3.5 h-3.5 text-sky-400 shrink-0" />
            <span className="flex-1">
              Überschrift über dem Brief mitschreiben
              <span className="text-muted-foreground/60"> (Text aus den iAuto-Einstellungen)</span>
            </span>
            <Switch checked={headlineOn} disabled={hlBusy || loading}
              onCheckedChange={toggleHeadline} data-testid="print-headline-toggle" />
          </div>
        )}
        {adjust && (
          <p className="text-[11px] text-muted-foreground border border-amber-500/30 bg-amber-500/5 rounded-sm px-3 py-2 shrink-0"
            data-testid="print-adjust-hint">
            {doc === 'envelope'
              ? <>Ziehe die Empfängeradresse an die gewünschte Position auf dem Umschlag —
                gilt nur für diesen Brief. Danach <span className="text-amber-300">Speichern</span>.</>
              : <>Ziehe die farbigen Boxen (Datum, Headline, Text, Unterschrift), bis alles zum Briefpapier passt —
                gilt nur für diesen Brief. Danach <span className="text-amber-300">Speichern</span>.</>}
          </p>
        )}
        {doc === 'letter' && !loading && letterFit && (letterFit.applied || !letterFit.fits) && (
          letterFit.fits ? (
            <p className="text-[11px] text-sky-200 border border-sky-500/30 bg-sky-500/5 rounded-sm px-3 py-2 shrink-0 flex items-center gap-2"
              data-testid="print-letter-fit-hint">
              <Minimize2 className="w-3.5 h-3.5 text-sky-400 shrink-0" />
              <span>Auto-Fit: {[
                letterFit.beside_date ? (letterFit.on_date_line ? 'Anrede auf die Datumszeile gerückt' : `Text um ${letterFit.shift_up_mm} mm nach oben gerückt (Anrede neben dem Datum)`) : null,
                letterFit.signature_scale < 1 ? `Unterschrift auf ${Math.round(letterFit.signature_scale * 100)} % verkleinert (${letterFit.signature_height_mm} statt ${letterFit.base_signature_height_mm} mm)` : null,
                letterFit.font_size < letterFit.base_font_size ? `Schrift auf ${letterFit.font_size} mm verkleinert (Standard ${letterFit.base_font_size} mm)` : null,
              ].filter(Boolean).join(' und ')},
                damit Brief und Unterschrift auf eine Seite passen — die Unterschrift steht unter dem letzten Absatz.
                {letterFit.margin_bottom_mm != null && <span className="text-muted-foreground"> Textgrenze bei {letterFit.bottom_mm} mm (Rand unten {letterFit.margin_bottom_mm} mm, iAuto-Einstellungen).</span>}
              </span>
            </p>
          ) : (
            <p className="text-[11px] text-red-200 border border-red-500/40 bg-red-500/10 rounded-sm px-3 py-2 shrink-0 flex items-center gap-2"
              data-testid="print-letter-fit-warning">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
              <span>
                {letterFit.reason === 'min_not_below_base'
                  ? <>Passt auch mit Anrede auf der Datumszeile und 20 % kleinerer Unterschrift nicht; Schrift verkleinern geht nicht: „min. Schrift“ ({letterFit.min_font_size} mm) ist nicht kleiner als die Standardgröße ({letterFit.base_font_size} mm) — in den iAuto-Einstellungen anpassen. </>
                  : <>Passt auch mit {letterFit.min_font_size} mm Schrift nicht auf eine Seite — </>}
                wird auf <span className="font-mono">{letterFit.pages} Seiten</span> geschrieben.
                {letterFit.margin_bottom_mm != null && <> Textgrenze bei {letterFit.bottom_mm} mm (Rand unten {letterFit.margin_bottom_mm} mm) — Rand verkleinern oder Brief kürzen.</>}
              </span>
            </p>
          )
        )}
        {doc === 'envelope' && !loading && (() => {
          const el = adjust ? (elements.env_addr || elements.env_addr_fixed) : autoFit;
          if (!el || (!el.auto_shift_mm && !el.auto_font_mm && !el.auto_wrapped)) return null;
          const parts = [
            el.auto_shift_mm ? `um ${Math.abs(el.auto_shift_mm)} mm nach ${el.auto_shift_mm > 0 ? 'links' : 'rechts'} gerückt` : '',
            el.auto_wrapped ? 'lange Zeile auf zwei Zeilen umbrochen' : '',
            el.auto_font_mm ? `Schrift auf ${el.auto_font_mm} mm verkleinert` : '',
          ].filter(Boolean);
          return (
            <p className="text-[11px] text-sky-300/90 border border-sky-500/30 bg-sky-500/5 rounded-sm px-3 py-2 shrink-0"
              data-testid="print-env-autofit-hint">
              Auto-Fit{el.anchor === 'right' ? ' (rechts an der Kante)' : ''}: Adresse {parts.join(', ')} — hält {el.right_margin_mm} mm Abstand zur rechten Kante (Settings → Umschlag).
            </p>
          );
        })()}
        <div ref={setAreaEl} className="flex-1 min-h-[160px] relative" data-testid="print-preview-area">
          {scale > 0 && (
            <div ref={pageRef}
              className={`bg-white rounded-sm border absolute overflow-hidden ${adjust ? 'border-amber-500/60 select-none' : 'border-border'}`}
              style={{ width: `${pageW}px`, height: `${pageH}px`, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
              {doc === 'letter' && (
                <img src={letter?.letter_kind === 'breakup' ? '/breakup-briefpapier.png' : '/briefpapier.png'} alt="Briefpapier"
                  data-testid="print-preview-letterhead"
                  className="absolute inset-0 w-full h-full pointer-events-none select-none" />
              )}
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              )}
              {svg
                ? <div className="absolute inset-0" dangerouslySetInnerHTML={{ __html: svg }} />
                : !loading && (
                  <p className="text-[11px] text-red-500 p-4" data-testid="print-preview-error">
                    {error || 'Preview unavailable'}
                  </p>
                )}
              {adjust && doc === 'letter' && elements.limits && (
                <div className="absolute left-0 right-0 border-t border-dashed border-red-400/70 pointer-events-none" data-testid="print-adjust-limit-line"
                  style={{ top: `${(elements.limits.bottom / dims.h) * 100}%` }}>
                  <span className="absolute right-1 -top-[14px] text-[8px] font-mono px-1 rounded-sm bg-red-500/80 text-white whitespace-nowrap">
                    Textgrenze {elements.limits.bottom} mm · Rand unten {elements.limits.margin_bottom} mm (iAuto-Einstellungen)
                  </span>
                </div>
              )}
              {visibleBoxes.map(key => {
                const el = elements[key];
                const pos = drag && drag.key === key ? drag : el;
                return (
                  <div key={key} onPointerDown={(e) => startDrag(key, e)} data-testid={`print-adjust-drag-${key}`}
                    className={`absolute border border-dashed rounded-[2px] cursor-move ${ADJ_ELEMENTS[key].cls} opacity-60 hover:opacity-100 transition-opacity`}
                    style={{
                      left: `${(pos.x / dims.w) * 100}%`, top: `${(pos.y / dims.h) * 100}%`,
                      width: `${(el.w / dims.w) * 100}%`, height: `${(el.h / dims.h) * 100}%`,
                    }}>
                    <span className={`absolute -top-[14px] left-0 text-[8px] font-mono px-1 rounded-sm text-white whitespace-nowrap ${ADJ_ELEMENTS[key].badge}`}>
                      {ADJ_ELEMENTS[key].label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="shrink-0 space-y-2">
          {checkFlow && !adjust && (
            <div className="flex items-center gap-2 flex-wrap border border-emerald-500/25 bg-emerald-500/5 rounded-sm px-3 py-2"
              data-testid="print-check-row">
              <span className="text-[11px] text-muted-foreground">
                Position geprüft? <span className="font-mono text-foreground">{checkedCount}/{docCycle.length}</span>
              </span>
              <span className="ml-auto flex items-center gap-2">
                {localChecks[doc] ? (
                  <>
                    <span className="text-[11px] text-emerald-400 flex items-center gap-1" data-testid="print-check-done-label">
                      <CheckCheck className="w-3.5 h-3.5" /> {DOC_LABELS[doc]} geprüft
                    </span>
                    <button onClick={unmarkChecked} disabled={checkBusy} data-testid="print-check-remove-btn"
                      className="text-[10px] text-muted-foreground hover:text-red-400 underline underline-offset-2">
                      Häkchen entfernen
                    </button>
                    <Button size="sm" variant="outline" onClick={() => advanceAfter(localChecks)} disabled={checkBusy}
                      data-testid="print-check-next-btn"
                      className="h-8 text-xs rounded-sm border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                      Weiter <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <Button size="sm" onClick={markChecked} disabled={checkBusy || loading}
                    data-testid="print-check-btn"
                    className="h-8 text-xs rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
                    {checkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    {DOC_LABELS[doc]} geprüft — weiter
                  </Button>
                )}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            {pages > 1 && (
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-mono">
                <button onClick={() => goPage(Math.max(1, page - 1))} disabled={page <= 1}
                  className="p-1 hover:text-foreground disabled:opacity-30" data-testid="print-preview-prev">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                Page {page}/{pages}
                <button onClick={() => goPage(Math.min(pages, page + 1))} disabled={page >= pages}
                  className="p-1 hover:text-foreground disabled:opacity-30" data-testid="print-preview-next">
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
            {adjust ? (
              <span className="ml-auto flex gap-2 items-center flex-wrap">
                {doc === 'letter' && (
                  <span className="flex items-center gap-1.5">
                    <label className="text-[10px] text-muted-foreground whitespace-nowrap">Brief-Datum:</label>
                    <input type="date" value={ov.date_override || ''} data-testid="print-adjust-date-input"
                      onChange={e => setOvVal('date_override', e.target.value || undefined)}
                      className="h-8 text-[11px] rounded-sm border border-border bg-secondary/40 text-foreground px-1.5" />
                    {ov.date_override && (
                      <button onClick={() => setOvVal('date_override', undefined)} data-testid="print-adjust-date-reset"
                        className="text-[10px] text-muted-foreground hover:text-foreground underline">heute</button>
                    )}
                  </span>
                )}
                {hasOv && (
                  <Button size="sm" variant="outline" onClick={resetOverrides} data-testid="print-adjust-reset-btn"
                    className="h-8 text-xs rounded-sm border-border text-muted-foreground">
                    <Undo2 className="w-3.5 h-3.5" /> Zurücksetzen
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={exitAdjust} data-testid="print-adjust-cancel-btn"
                  className="h-8 text-xs rounded-sm border-border">Abbrechen</Button>
                <Button size="sm" onClick={saveOverrides} disabled={savingOv} data-testid="print-adjust-save-btn"
                  className="h-8 text-xs rounded-sm bg-amber-600 hover:bg-amber-500 text-white">
                  {savingOv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Speichern
                </Button>
              </span>
            ) : (
              <span className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={onClose} data-testid="print-preview-cancel-btn"
                  className="h-8 text-xs rounded-sm border-border">{onConfirm ? 'Cancel' : 'Schließen'}</Button>
                {onConfirm && (
                  <Button size="sm" onClick={() => onConfirm(letter, doc)} disabled={confirming || loading || !svg}
                    data-testid="print-preview-confirm-btn"
                    className="h-8 text-xs rounded-sm bg-purple-600 hover:bg-purple-500 text-white">
                    {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PenLine className="w-3.5 h-3.5" />}
                    Send to iAuto
                  </Button>
                )}
              </span>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrintPreviewDialog;
